import { Box, Button, Grid, Input, Modal, IconButton, Snackbar, Alert } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import SearchIcon from '@mui/icons-material/Search';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import ArrowUpwardIcon from '@mui/icons-material/ArrowUpward';
import ArrowDownwardIcon from '@mui/icons-material/ArrowDownward';
import React from 'react';
import { useSelector } from 'react-redux';
import { FixedSizeList as List } from 'react-window';
import { LateSwapLineup, LateSwapPlayer } from './store/types';
import RoiBoostButton from '../common/Table/RoiBoostButton';
import { useLocalStorage } from 'usehooks-ts';
import { showdownPositions } from '../utils/showdownHandler';
import { isTeamSport } from '../utils/teamSportHelper';
import { findLineupsToFavorite } from './utils';
import { SlateResponse } from '../common/store/types';
import { Projection } from '../common/EasyProjection/types';
import { IRootState } from '../../store/reducer';
import { GridApiPro } from '@mui/x-data-grid-pro/models/gridApiPro';
import { gridFilteredSortedRowEntriesSelector } from '@mui/x-data-grid-pro';

// Reusable Components
type SortDirection = 'asc' | 'desc' | null;

const HeaderCell = ({
  children,
  center = false,
  sortable = false,
  sortDirection = null,
  onSort,
}: {
  children: React.ReactNode;
  center?: boolean;
  sortable?: boolean;
  sortDirection?: SortDirection;
  onSort?: () => void;
}) => (
  <Box
    onClick={sortable ? onSort : undefined}
    sx={{
      fontSize: 11,
      fontWeight: 700,
      color: 'rgba(255, 255, 255, 0.70)',
      textTransform: 'uppercase',
      padding: '0 8px',
      textAlign: center ? 'center' : 'left',
      cursor: sortable ? 'pointer' : 'default',
      display: 'flex',
      alignItems: 'center',
      justifyContent: center ? 'center' : 'flex-start',
      gap: 0.5,
      '&:hover': sortable ? {
        color: 'rgba(255, 255, 255, 0.90)',
      } : {},
    }}
  >
    {children}
    {sortable && sortDirection === 'asc' && <ArrowUpwardIcon sx={{ fontSize: 14 }} />}
    {sortable && sortDirection === 'desc' && <ArrowDownwardIcon sx={{ fontSize: 14 }} />}
  </Box>
);

const DataCell = ({ children, center = false, bold = false }: { children: React.ReactNode; center?: boolean; bold?: boolean }) => (
  <Box sx={{
    display: 'flex',
    alignItems: 'center',
    justifyContent: center ? 'center' : 'flex-start',
    fontSize: 13,
    padding: '0 4px',
    fontWeight: bold ? 700 : 400,
    height: '100%',
  }}>
    {children}
  </Box>
);

const PositionPill = ({ active, onClick, children }: { active: boolean; onClick: () => void; children: React.ReactNode }) => (
  <Box
    onClick={onClick}
    sx={{
      padding: '6px 12px',
      fontSize: 12,
      fontWeight: 500,
      cursor: 'pointer',
      borderRadius: 1.5,
      backgroundColor: active ? 'rgba(219, 77, 0, 0.70)' : 'transparent',
      color: active ? 'white' : 'rgba(255, 255, 255, 0.7)',
      '&:hover': {
        backgroundColor: active ? 'rgba(219, 77, 0, 0.70)' : 'rgba(219, 77, 0, 0.3)',
      },
    }}
  >
    {children}
  </Box>
);

interface OwnProps {
  readonly open: boolean;
  readonly players: LateSwapPlayer[];
  readonly playerRoiList: { [key: string]: number };
  readonly playerExposureList: { [key: string]: number };
  readonly setPlayerRoiList: (list: { [key: string]: number }) => void;
  readonly setPlayerExposureList: (list: { [key: string]: number }) => void;
  readonly closeModal: () => void;
  readonly selectedSlate: SlateResponse;
  readonly lineups: LateSwapLineup[];
  readonly contestName: string;
  readonly allowDupes: boolean;
  readonly stackExposureDict: { [key: string]: number };
  readonly customProjections?: Projection[];
  readonly apiRef?: React.MutableRefObject<GridApiPro> | null;
}

const LateSwapPlayerManagementModal = (props: OwnProps) => {
  const {
    open,
    players,
    playerRoiList,
    playerExposureList,
    setPlayerRoiList,
    setPlayerExposureList,
    closeModal,
    selectedSlate,
    lineups,
    contestName,
    allowDupes,
    stackExposureDict,
    customProjections = [],
    apiRef,
  } = props;

  const sport = selectedSlate?.sport;
  const slateType = selectedSlate?.type;
  const teamSport = isTeamSport(sport);
  const showdown = slateType?.toUpperCase() === 'SHOWDOWN';

  // Get unique limit from Redux state (set by QuickFavorite)
  const { uniqueLimit } = useSelector((state: IRootState) => state.common);

  const [updatedRoiList, setUpdatedRoiList] = React.useState<{ [key: string]: number }>(playerRoiList);
  const [updatedExposureList, setUpdatedExposureList] = React.useState<{ [key: string]: number }>(playerExposureList);
  const [filteredPlayers, setFilteredPlayers] = React.useState<LateSwapPlayer[]>([]);
  const [searchVal, setSearchVal] = React.useState('');
  const [selectedPosition, setSelectedPosition] = React.useState<string>('');
  const [mlbPlayerType, setMlbPlayerType] = React.useState<'all' | 'batters' | 'pitchers'>('all');
  const [globalMaxExposure, setGlobalMaxExposure] = React.useState<number>(100);
  const [snackbarOpen, setSnackbarOpen] = React.useState(false);
  const [exposuresCleared, setExposuresCleared] = React.useState(false);
  const [sortColumn, setSortColumn] = useLocalStorage<string>('lateswap-player-modal-sort-column', 'postSwapExposure');
  const [sortDirection, setSortDirection] = useLocalStorage<SortDirection>('lateswap-player-modal-sort-direction', 'desc');

  const [savedLineups, setSavedLineups] = useLocalStorage<LateSwapLineup[]>(
    "lateswap-lineups",
    []
  );

  const getPlayerKey = React.useCallback((player: LateSwapPlayer) => {
    const positionCaps = player.position?.toUpperCase();
    return showdownPositions.includes(positionCaps) ? player.nameAndId + '||' + positionCaps : player.nameAndId;
  }, []);

  // Calculate current exposure for each player based on savedLineups
  const computedExposures = React.useMemo(() => {
    const exposureMap: { [key: string]: number } = {};
    if (savedLineups.length === 0) return exposureMap;

    savedLineups.forEach(lineup => {
      if (!lineup?.exportableLineup) return;
      const lineupPlayers = lineup.exportableLineup.split(',').map((p: string) => p.trim());
      lineupPlayers.forEach((player: string, index: number) => {
        const key = showdown
          ? (index === 0 ? `${player}||CPT` : `${player}||FLEX`)
          : player;
        exposureMap[key] = (exposureMap[key] || 0) + 1;
      });
    });

    // Convert counts to percentages
    const totalLineups = savedLineups.length;
    Object.keys(exposureMap).forEach(key => {
      exposureMap[key] = (exposureMap[key] / totalLineups) * 100;
    });

    return exposureMap;
  }, [savedLineups, showdown]);

  // Get computed exposure for a player
  const getComputedExposure = React.useCallback((player: LateSwapPlayer) => {
    const key = getPlayerKey(player);
    return computedExposures[key] || 0;
  }, [computedExposures, getPlayerKey]);

  const sortPlayers = React.useCallback((playerList: LateSwapPlayer[]) => {
    const multiplier = sortDirection === 'asc' ? 1 : -1;
    return [...playerList].sort((a, b) => {
      switch (sortColumn) {
        case 'player':
          return multiplier * (a.name || '').localeCompare(b.name || '');
        case 'position':
          return multiplier * (a.position || '').localeCompare(b.position || '');
        case 'team':
          return multiplier * (a.team || '').localeCompare(b.team || '');
        case 'projection':
          return multiplier * ((parseFloat(String(a.projection)) || 0) - (parseFloat(String(b.projection)) || 0));
        case 'ownership':
          return multiplier * ((a.ownership || 0) - (b.ownership || 0));
        case 'originalExposure':
          return multiplier * ((a.originalExposure || 0) - (b.originalExposure || 0));
        case 'postSwapExposure':
          return multiplier * (getComputedExposure(a) - getComputedExposure(b));
        case 'roiBoost':
          return multiplier * ((updatedRoiList[getPlayerKey(a)] || 0) - (updatedRoiList[getPlayerKey(b)] || 0));
        case 'exposureCap':
          return multiplier * ((updatedExposureList[getPlayerKey(a)] ?? 100) - (updatedExposureList[getPlayerKey(b)] ?? 100));
        default:
          return multiplier * (getComputedExposure(a) - getComputedExposure(b));
      }
    });
  }, [sortColumn, sortDirection, updatedRoiList, updatedExposureList, getPlayerKey, getComputedExposure]);

  const handleSort = (column: string) => {
    if (sortColumn === column) {
      setSortDirection(prev => prev === 'asc' ? 'desc' : 'asc');
    } else {
      setSortColumn(column);
      setSortDirection('desc');
    }
  };

  React.useEffect(() => {
    setFilteredPlayers(sortPlayers(players));
  }, [players, sortPlayers]);

  React.useEffect(() => {
    setUpdatedRoiList(playerRoiList);
  }, [playerRoiList]);

  React.useEffect(() => {
    setUpdatedExposureList(playerExposureList);
  }, [playerExposureList]);

  React.useEffect(() => {
    let result = sortPlayers(players);
    if (searchVal) {
      result = result.filter(x => x.name?.toLowerCase().includes(searchVal.toLowerCase()));
    }
    if (selectedPosition) {
      result = result.filter(x => x.position?.toLowerCase().includes(selectedPosition.toLowerCase()));
    }
    // MLB-specific filter for batters vs pitchers
    if (sport?.toLowerCase() === 'mlb' && mlbPlayerType !== 'all') {
      if (mlbPlayerType === 'pitchers') {
        result = result.filter(x => x.position?.toUpperCase().includes('P'));
      } else {
        // Batters - exclude pitchers
        result = result.filter(x => !x.position?.toUpperCase().includes('P'));
      }
    }
    setFilteredPlayers(result);
  }, [searchVal, selectedPosition, mlbPlayerType, players, sortPlayers, sport]);

  const handleRoiChange = React.useCallback((player: string, position: string, percent: number) => {
    const positionCaps = position?.toUpperCase();
    const key = showdownPositions.includes(positionCaps) ? player + '||' + positionCaps : player;
    setUpdatedRoiList(prev => ({ ...prev, [key]: percent }));
  }, []);

  const handleExposureChange = React.useCallback((player: string, position: string, percent: number) => {
    const positionCaps = position?.toUpperCase();
    const key = showdownPositions.includes(positionCaps) ? player + '||' + positionCaps : player;
    setUpdatedExposureList(prev => ({ ...prev, [key]: percent }));
  }, []);

  const handleGlobalMaxExposureChange = (percent: number) => {
    setGlobalMaxExposure(percent);
    // Store global exposure as a separate key, don't overwrite individual player exposures
    setUpdatedExposureList(prev => ({ ...prev, global: percent }));
  };

  const getRoiPercent = React.useCallback((player: string, position: string) => {
    const positionCaps = position?.toUpperCase();
    const key = showdownPositions.includes(positionCaps) ? player + '||' + positionCaps : player;
    return updatedRoiList[key] !== undefined ? updatedRoiList[key] : 0;
  }, [updatedRoiList]);

  const getExposurePercent = React.useCallback((player: string, position: string) => {
    const positionCaps = position?.toUpperCase();
    const key = showdownPositions.includes(positionCaps) ? player + '||' + positionCaps : player;

    // First check if there's an explicit value in the exposure list
    if (updatedExposureList[key] !== undefined) {
      return updatedExposureList[key];
    }

    // If exposures were cleared, don't fall back to customProjections
    if (exposuresCleared) {
      return 100;
    }

    // Then check customProjections for maxExposure
    if (customProjections && customProjections.length > 0) {
      const projection = customProjections.find(p => p.nameAndId === player);
      if (projection?.maxExposure !== undefined) {
        return projection.maxExposure * 100; // Convert from decimal to percentage
      }
    }

    return 100; // Default to 100%
  }, [updatedExposureList, customProjections, exposuresCleared]);

  const handleClearExposures = () => {
    setUpdatedExposureList({});
    setPlayerExposureList({});
    setGlobalMaxExposure(100);
    setExposuresCleared(true); // Mark that exposures were explicitly cleared

    // Refavorite lineups with cleared exposures
    // Don't clear savedLineups first to avoid visual flash
    if (savedLineups.length > 0) {
      setTimeout(() => {
        const result = findLineupsToFavorite(
          lineups,
          players,
          [],
          {}, // Empty exposure list
          stackExposureDict,
          updatedRoiList, // Keep current ROI boosts
          [],
          contestName,
          selectedSlate,
          allowDupes,
          undefined, // boostedStacks
          uniqueLimit || undefined
        );
        setSavedLineups(result.lineups as LateSwapLineup[]);
        setSnackbarOpen(true);
      }, 100);
    }
  };

  const handleClearBoosts = () => {
    setUpdatedRoiList({});
    setPlayerRoiList({});

    // Refavorite lineups with cleared boosts
    // Don't clear savedLineups first to avoid visual flash
    if (savedLineups.length > 0) {
      setTimeout(() => {
        const result = findLineupsToFavorite(
          lineups,
          players,
          [],
          updatedExposureList, // Keep current exposure caps
          stackExposureDict,
          {}, // Empty ROI list
          [],
          contestName,
          selectedSlate,
          allowDupes,
          undefined, // boostedStacks
          uniqueLimit || undefined
        );
        setSavedLineups(result.lineups as LateSwapLineup[]);
        setSnackbarOpen(true);
      }, 100);
    }
  };

  const handleAdjustFavorites = () => {
    const filteredRoiList: { [key: string]: number } = {};
    const filteredExposureList: { [key: string]: number } = {};

    Object.keys(updatedRoiList).forEach(key => {
      if (updatedRoiList[key] !== 0) {
        filteredRoiList[key] = updatedRoiList[key];
      }
    });

    // First, add exposures from customProjections (uploaded maxExposure values)
    // Only if exposures haven't been explicitly cleared
    if (!exposuresCleared && customProjections && customProjections.length > 0) {
      customProjections.forEach(projection => {
        if (projection.maxExposure !== undefined && projection.maxExposure !== 1) {
          // Only add if not already explicitly set in updatedExposureList
          if (updatedExposureList[projection.nameAndId] === undefined) {
            filteredExposureList[projection.nameAndId] = projection.maxExposure * 100;
          }
        }
      });
    }

    // Then add/override with any explicit user changes from the modal
    Object.keys(updatedExposureList).forEach(key => {
      if (updatedExposureList[key] !== 100 && updatedExposureList[key] !== undefined) {
        filteredExposureList[key] = updatedExposureList[key];
      }
    });

    setPlayerRoiList(filteredRoiList);
    setPlayerExposureList(filteredExposureList);

    // Refavorite lineups when we have saved lineups
    // Don't clear savedLineups first to avoid visual flash
    if (savedLineups.length > 0) {
      setTimeout(() => {
        // Get filtered lineups from the grid if filters are applied
        let lineupsToUse = lineups;
        try {
          if (apiRef?.current) {
            const filteredRows = gridFilteredSortedRowEntriesSelector(apiRef);
            // Check if the grid contains lineup data (has lateSwapOptions or similar)
            if (filteredRows.length > 0 && filteredRows.length < lineups.length) {
              // Create a set of visible lineup exportableLineups for quick lookup
              const visibleLineupIds = new Set(filteredRows.map(row => row.model?.id));
              lineupsToUse = lineups.filter(l => visibleLineupIds.has(l.id));
            }
          }
        } catch (e) {
          // Grid not available or error - proceed without filtering
        }

        const result = findLineupsToFavorite(
          lineupsToUse,
          players,
          [],
          filteredExposureList,
          stackExposureDict,
          filteredRoiList,
          [],
          contestName,
          selectedSlate,
          allowDupes,
          undefined, // boostedStacks
          uniqueLimit || undefined
        );
        setSavedLineups(result.lineups as LateSwapLineup[]);
        setSnackbarOpen(true);
        // Re-sort to reflect updated exposures
        setFilteredPlayers(sortPlayers(players));
      }, 100);
    } else {
      setSnackbarOpen(true);
    }
  };

  const getPositions = () => {
    const { site, sport, type } = selectedSlate;

    if (
      site?.toLowerCase() === 'fd' &&
      ['nba', 'mlb', 'nfl'].indexOf(sport?.toLowerCase()) >= 0 &&
      type?.toLowerCase() === 'showdown'
    ) {
      return ['CPT', 'FLEX'];
    } else if (
      site?.toLowerCase() === 'dk' &&
      ['nba'].indexOf(sport?.toLowerCase()) >= 0 &&
      type?.toLowerCase() === 'showdown'
    ) {
      return ['CPT', 'FLEX'];
    }

    // Get positions from lineup rules
    const lineupPositions = selectedSlate?.lineupRules?.positions
      ?.split(',')
      .filter((value, index, self) => self.indexOf(value) === index) || [];

    // Filter out flex/utility positions that don't map to actual player positions
    // These are lineup slots, not player positions, so filtering by them returns no results
    // On showdown slates FLEX is a real player position (rows are tagged CPT/FLEX), so keep it as a tab
    const flexPositions = type?.toLowerCase() === 'showdown' ? ['UTIL', 'G', 'F'] : ['UTIL', 'G', 'F', 'FLEX', 'G/F', 'F/C'];
    const filteredPositions = lineupPositions.filter(pos => !flexPositions.includes(pos.toUpperCase()));

    // Map lineup rule positions to actual player positions for filtering (only for FanDuel)
    if (site?.toLowerCase() === 'fd') {
      const positionMapping: { [key: string]: string } = { 'DEF': 'D' };
      return filteredPositions.map(pos => positionMapping[pos] || pos);
    }

    return filteredPositions;
  };

  const getPlayerColumnWidth = () => {
    if (teamSport) return 2.5;
    if (showdown) return 3.5;
    return 4.5;
  };

  const ROW_HEIGHT = 64;
  const LIST_HEIGHT = 300;

  // Item data for react-window - must include all data needed for rendering
  // This ensures rows re-render when these values change
  const itemData = React.useMemo(() => ({
    filteredPlayers,
    teamSport,
    showdown,
    getPlayerColumnWidth,
    handleRoiChange,
    handleExposureChange,
    getRoiPercent,
    getExposurePercent,
    getComputedExposure,
  }), [filteredPlayers, teamSport, showdown, handleRoiChange, handleExposureChange, getRoiPercent, getExposurePercent, getComputedExposure]);

  // Row renderer for virtualization - uses itemData for dependencies
  const PlayerRow = ({ index, style, data }: { index: number; style: React.CSSProperties; data: typeof itemData }) => {
    const player = data.filteredPlayers[index];
    if (!player) return null;

    return (
      <Box
        style={style}
        sx={{
          display: 'flex',
          alignItems: 'center',
          borderBottom: '1px solid rgba(255, 255, 255, 0.1)',
          '&:hover': { backgroundColor: 'rgba(255, 255, 255, 0.02)' },
        }}
      >
        <Grid container alignItems="center" sx={{ height: '100%' }}>
          <Grid item xs={data.getPlayerColumnWidth()}>
            <DataCell>
              <Box>
                <Box component="span" sx={{ color: 'rgba(255, 255, 255, 0.70)', fontSize: 12, textTransform: 'uppercase' }}>
                  {player.name?.split(' ')[0]}
                </Box>{' '}
                <Box component="span" sx={{ fontSize: 14, fontWeight: 700, textTransform: 'uppercase' }}>
                  {player.name?.split(' ')?.slice(1).join(' ')}
                </Box>
              </Box>
            </DataCell>
          </Grid>
          {(data.teamSport || data.showdown) && <Grid item xs={1}><DataCell>{player.position}</DataCell></Grid>}
          {data.teamSport && <Grid item xs={1}><DataCell>{player.team || '-'}</DataCell></Grid>}
          <Grid item xs={1}><DataCell>{player.projection ? parseFloat(String(player.projection)).toFixed(2) : '-'}</DataCell></Grid>
          <Grid item xs={1}><DataCell>{player.ownership ? `${(player.ownership * 100).toFixed(1)}%` : '-'}</DataCell></Grid>
          <Grid item xs={1}><DataCell>{player.originalExposure ? `${(player.originalExposure * 100).toFixed(1)}%` : '0%'}</DataCell></Grid>
          <Grid item xs={1}><DataCell center bold>{`${data.getComputedExposure(player).toFixed(1)}%`}</DataCell></Grid>
          <Grid item xs={1.5}>
            <DataCell>
              <RoiBoostButton
                defaultPercent={0}
                onChange={(percent) => data.handleRoiChange(player.nameAndId, player.position, percent)}
                previousPercent={data.getRoiPercent(player.nameAndId, player.position)}
              />
            </DataCell>
          </Grid>
          <Grid item xs={1.5}>
            <DataCell>
              <RoiBoostButton
                defaultPercent={100}
                onChange={(percent) => data.handleExposureChange(player.nameAndId, player.position, percent)}
                previousPercent={data.getExposurePercent(player.nameAndId, player.position)}
                constantMax={100}
                constantMin={0}
              />
            </DataCell>
          </Grid>
        </Grid>
      </Box>
    );
  };

  return (
    <>
      <Modal
        open={open}
        disableAutoFocus
        onClose={(_event: object, reason: string) => {
          if (reason?.toLowerCase() === 'backdropclick') closeModal();
        }}
        sx={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          color: 'white',
          fontFamily: 'Roboto',
          backgroundColor: 'rgba(125, 125, 125, .65)',
        }}
      >
        <Box sx={{
          backgroundColor: '#0A0A0A',
          borderRadius: 2,
          minHeight: 400,
          width: 1050,
          overflow: 'hidden',
          border: '1px solid rgba(255, 255, 255, .30)',
        }}>
          {/* Header */}
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', padding: '20px 20px 0 20px' }}>
            <Box>
              <Box sx={{ fontSize: 20, fontWeight: 600, marginBottom: 1 }}>Player Management</Box>
              <Box sx={{ fontSize: 14, fontWeight: 400, color: 'rgba(255, 255, 255, 0.7)', maxWidth: 600, lineHeight: 1.4 }}>
                Add positive or negative ROI boosts to lineups that include this player to influence ROI rankings.
                You can also set exposure caps to limit how often the player appears.
              </Box>
            </Box>
            <IconButton onClick={closeModal} sx={{ color: 'white' }}>
              <CloseIcon />
            </IconButton>
          </Box>

          {/* Search */}
          <Box sx={{ margin: '16px 20px', backgroundColor: 'rgba(43, 48, 62, 0.15)', border: '1px solid #2C3140', borderRadius: 1 }}>
            <Input
              value={searchVal}
              onChange={(e) => setSearchVal(e.target.value)}
              placeholder="Search Players"
              disableUnderline
              endAdornment={<SearchIcon style={{ color: 'rgba(255,255,255,0.5)' }} />}
              sx={{ color: 'rgba(255, 255, 255, 0.70)', width: '100%', padding: '8px 12px' }}
            />
          </Box>

          {/* Position Filters */}
          <Box sx={{ display: 'flex', gap: 1, padding: '0 20px 16px 20px', flexWrap: 'wrap', alignItems: 'center' }}>
            <PositionPill active={!selectedPosition} onClick={() => setSelectedPosition('')}>ALL</PositionPill>
            {getPositions().map((position) => (
              <PositionPill key={position} active={selectedPosition === position} onClick={() => setSelectedPosition(position)}>
                {position}
              </PositionPill>
            ))}
            {/* MLB Batters/Pitchers Toggle */}
            {sport?.toLowerCase() === 'mlb' && (
              <>
                <Box sx={{ width: 1, height: '1px', backgroundColor: 'rgba(255, 255, 255, 0.2)', mx: 1 }} />
                <PositionPill active={mlbPlayerType === 'all'} onClick={() => setMlbPlayerType('all')}>All Players</PositionPill>
                <PositionPill active={mlbPlayerType === 'batters'} onClick={() => setMlbPlayerType('batters')}>Batters</PositionPill>
                <PositionPill active={mlbPlayerType === 'pitchers'} onClick={() => setMlbPlayerType('pitchers')}>Pitchers</PositionPill>
              </>
            )}
          </Box>

          {/* Table Header */}
          <Box sx={{ backgroundColor: 'rgba(219, 77, 0, 0.15)', borderBottom: '1px solid rgba(219, 77, 0, .8)', padding: '8px 0', margin: '0 20px' }}>
            <Grid container alignItems="center">
              <Grid item xs={getPlayerColumnWidth()}>
                <HeaderCell sortable sortDirection={sortColumn === 'player' ? sortDirection : null} onSort={() => handleSort('player')}>Player</HeaderCell>
              </Grid>
              {(teamSport || showdown) && <Grid item xs={1}>
                <HeaderCell sortable sortDirection={sortColumn === 'position' ? sortDirection : null} onSort={() => handleSort('position')}>Pos.</HeaderCell>
              </Grid>}
              {teamSport && <Grid item xs={1}>
                <HeaderCell sortable sortDirection={sortColumn === 'team' ? sortDirection : null} onSort={() => handleSort('team')}>Team</HeaderCell>
              </Grid>}
              <Grid item xs={1}>
                <HeaderCell sortable sortDirection={sortColumn === 'projection' ? sortDirection : null} onSort={() => handleSort('projection')}>Proj.</HeaderCell>
              </Grid>
              <Grid item xs={1}>
                <HeaderCell sortable sortDirection={sortColumn === 'ownership' ? sortDirection : null} onSort={() => handleSort('ownership')}>Own. %</HeaderCell>
              </Grid>
              <Grid item xs={1}>
                <HeaderCell sortable sortDirection={sortColumn === 'originalExposure' ? sortDirection : null} onSort={() => handleSort('originalExposure')}>Pre-Swap Exp</HeaderCell>
              </Grid>
              <Grid item xs={1}>
                <HeaderCell center sortable sortDirection={sortColumn === 'postSwapExposure' ? sortDirection : null} onSort={() => handleSort('postSwapExposure')}>Post-Swap Exp</HeaderCell>
              </Grid>
              <Grid item xs={1.5}>
                <HeaderCell center sortable sortDirection={sortColumn === 'roiBoost' ? sortDirection : null} onSort={() => handleSort('roiBoost')}>ROI Boost</HeaderCell>
              </Grid>
              <Grid item xs={1.5}>
                <HeaderCell center sortable sortDirection={sortColumn === 'exposureCap' ? sortDirection : null} onSort={() => handleSort('exposureCap')}>Exposure Cap</HeaderCell>
              </Grid>
            </Grid>
          </Box>

          {/* Virtualized Table Body */}
          <Box sx={{ margin: '0 20px', overflow: 'hidden' }}>
            <List
              height={LIST_HEIGHT}
              itemCount={filteredPlayers.length}
              itemSize={ROW_HEIGHT}
              itemData={itemData}
              width="100%"
              style={{
                scrollbarWidth: 'thin',
                scrollbarColor: 'slategrey transparent',
                overflowX: 'hidden',
              }}
            >
              {PlayerRow}
            </List>
          </Box>

          {/* Footer */}
          <Box sx={{ display: 'flex', justifyContent: 'flex-end', alignItems: 'center', gap: 2, padding: '16px 20px', borderTop: '1px solid rgba(255, 255, 255, 0.1)', backgroundColor: '#0A0A0A' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, fontSize: 14 }}>
              <span>Global Max Exposure:</span>
              <RoiBoostButton
                defaultPercent={100}
                onChange={handleGlobalMaxExposureChange}
                previousPercent={globalMaxExposure}
                constantMax={100}
                constantMin={0}
              />
            </Box>
            <Button
              variant="outlined"
              onClick={handleClearExposures}
              sx={{ color: 'white', borderColor: 'rgba(255, 255, 255, 0.3)', '&:hover': { borderColor: 'rgba(255, 255, 255, 0.5)', backgroundColor: 'rgba(255, 255, 255, 0.05)' } }}
            >
              CLEAR EXPOSURES
            </Button>
            <Button
              variant="outlined"
              onClick={handleClearBoosts}
              sx={{ color: 'white', borderColor: 'rgba(255, 255, 255, 0.3)', '&:hover': { borderColor: 'rgba(255, 255, 255, 0.5)', backgroundColor: 'rgba(255, 255, 255, 0.05)' } }}
            >
              CLEAR BOOSTS
            </Button>
            <Button
              variant="contained"
              endIcon={<OpenInNewIcon />}
              onClick={handleAdjustFavorites}
              sx={{ backgroundColor: 'rgba(219, 77, 0, 0.70)', color: 'white', '&:hover': { backgroundColor: 'rgba(219, 77, 0, 0.90)' } }}
            >
              ADJUST FAVORITES
            </Button>
          </Box>
        </Box>
      </Modal>
      <Snackbar
        open={snackbarOpen}
        autoHideDuration={3000}
        onClose={() => setSnackbarOpen(false)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert onClose={() => setSnackbarOpen(false)} severity="success" sx={{ width: '100%' }}>
          Favorites updated successfully!
        </Alert>
      </Snackbar>
    </>
  );
};

export default LateSwapPlayerManagementModal;
